import { useEffect, useState } from "react";
import api from "@/lib/api";
import { AttendanceStatusBadge, pctColor, DEFAULT_ATTENDANCE_THRESHOLD } from "@/lib/attendance";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { CalendarCheck, Info, TrendingUp, AlertTriangle } from "lucide-react";

const EMPTY = { overall: null, by_course: [], sessions: [], join_date: null };

export default function StudentAttendance() {
    const [data, setData] = useState(null);
    const [threshold, setThreshold] = useState(DEFAULT_ATTENDANCE_THRESHOLD);

    useEffect(() => {
        // /attendance/me is the only attendance route a student can reach, and it
        // takes no id — there is no way to ask for anyone else's record.
        api.get("/attendance/me").then(r => setData(r.data)).catch(() => setData(EMPTY));
        // Read the same threshold admins flag against so this page's colours and
        // the admin report never disagree. (GET /settings is open to any signed-in
        // user — see BACKLOG L3.)
        api.get("/settings")
            .then(r => { const t = parseFloat(r.data?.attendance_threshold); if (!Number.isNaN(t)) setThreshold(t); })
            .catch(() => {});
    }, []);

    if (!data) return <div className="text-muted-foreground">Loading…</div>;

    const overall = data.overall || { present: 0, late: 0, absent: 0, total: 0, percentage: 0 };
    const hasData = overall.total > 0;
    const below = hasData && overall.percentage < threshold;

    return (
        <div className="space-y-6" data-testid="student-attendance-page">
            <div>
                <div className="text-xs uppercase tracking-widest font-bold text-muted-foreground">
                    {data.batch_name || "My record"}
                </div>
                <h1 className="font-display text-3xl lg:text-4xl mt-1">My attendance</h1>
            </div>

            {/* Overall */}
            <Card className="mx-card overflow-hidden">
                <CardContent className="p-6">
                    <div className="flex flex-wrap items-center gap-6">
                        <div>
                            <div className="text-xs uppercase tracking-widest font-bold text-muted-foreground">Overall</div>
                            <div className={`font-display text-5xl mt-1 ${pctColor(overall.percentage, threshold)}`} data-testid="overall-percentage">
                                {hasData ? `${overall.percentage}%` : "—"}
                            </div>
                        </div>
                        <div className="flex-1 min-w-[200px]">
                            <Progress value={hasData ? overall.percentage : 0} className="h-2.5" />
                            <div className="flex flex-wrap gap-2 mt-3">
                                <Badge variant="secondary" data-testid="counted-total">{overall.total} class{overall.total === 1 ? "" : "es"} counted</Badge>
                                <Badge className="bg-[#16A34A] hover:bg-[#16A34A]">{overall.present} present</Badge>
                                <Badge className="bg-[#FFA81B] hover:bg-[#FFA81B]">{overall.late} late</Badge>
                                <Badge className="bg-[#E11D48] hover:bg-[#E11D48]">{overall.absent} absent</Badge>
                            </div>
                        </div>
                    </div>

                    {below && (
                        <div className="flex items-start gap-2 mt-5 text-xs text-[#E11D48]" data-testid="low-attendance-warning">
                            <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                            <span>You are below the {threshold}% attendance requirement.</span>
                        </div>
                    )}

                    {/* This is what stops a mid-batch joiner reading their number as a mistake. */}
                    {data.join_date && (
                        <div className="flex items-start gap-2 mt-3 text-xs text-muted-foreground" data-testid="join-date-note">
                            <Info className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                            <span>
                                Counted from <strong>{data.join_date}</strong>, the date you joined this batch. Classes held before then, and any
                                cancelled class, are not counted. A “late” mark counts as attended.
                            </span>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Per course */}
            {data.by_course.length > 0 && (
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {data.by_course.map(c => (
                        <Card key={c.course_id || "none"} className="mx-card" data-testid={`course-attendance-${c.course_id || "none"}`}>
                            <CardContent className="p-5">
                                <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase tracking-widest font-bold">
                                    <TrendingUp className="h-3.5 w-3.5" /> {c.course_title || "Course"}
                                </div>
                                <div className={`font-display text-3xl mt-2 ${pctColor(c.percentage, threshold)}`}>{c.percentage}%</div>
                                <Progress value={c.percentage} className="h-1.5 mt-3" />
                                <div className="text-xs text-muted-foreground mt-2">
                                    {c.present} present · {c.late} late · {c.absent} absent · {c.total} counted
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            )}

            {/* Session history */}
            <div>
                <h2 className="font-display text-xl mb-3">Class history</h2>
                <Card className="mx-card">
                    <CardContent className="p-0 overflow-x-auto">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Date</TableHead>
                                    <TableHead>Course</TableHead>
                                    <TableHead>Mode</TableHead>
                                    <TableHead>Status</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {data.sessions.map(s => (
                                    <TableRow key={s.session_id} data-testid={`attendance-session-${s.session_id}`}>
                                        <TableCell>
                                            <div className="font-semibold text-sm">{s.session_date}</div>
                                            <div className="text-xs text-muted-foreground">
                                                {s.start_time}{s.end_time ? `–${s.end_time}` : ""}
                                            </div>
                                        </TableCell>
                                        <TableCell className="text-sm">{s.course_title || "—"}</TableCell>
                                        <TableCell className="text-sm capitalize">{s.mode}</TableCell>
                                        <TableCell>
                                            <AttendanceStatusBadge status={s.status} />
                                            {/* An unmarked student in a completed session counts as absent;
                                                say so rather than implying they were marked absent. */}
                                            {!s.marked && (
                                                <div className="text-[10px] text-muted-foreground mt-1">not marked by faculty</div>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {data.sessions.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={4} className="text-center text-muted-foreground py-12">
                                            <CalendarCheck className="h-10 w-10 mx-auto mb-3 opacity-40" />
                                            No classes recorded yet.
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
